import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createUser,
  fetchUsers,
  resetUserPassword,
  resolveZentaoAccount,
  revokeUserSessions,
  syncUserZentaoAccount,
  unlockUser,
  updateUser,
} from '@/services/api/users'
import type { AppUserPasswordResetPayload, AppUserUpsertPayload } from '@/types/user'

export function useUserList(params: {
  page?: number
  page_size?: number
  keyword?: string
  status?: string
}) {
  return useQuery({
    queryKey: ['users', 'list', params],
    queryFn: () => fetchUsers(params),
    staleTime: 15_000,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: AppUserUpsertPayload }) =>
      updateUser(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['group-bindings', 'zentao-options'] })
    },
  })
}

export function useResolveZentaoAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resolveZentaoAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-bindings', 'zentao-options'] })
    },
  })
}

export function useSyncUserZentaoAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: syncUserZentaoAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['group-bindings', 'zentao-options'] })
    },
  })
}

export function useResetUserPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: AppUserPasswordResetPayload }) =>
      resetUserPassword(userId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUnlockUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: unlockUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useRevokeUserSessions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: revokeUserSessions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })
}
